/**
 * MegaMissions — timed quest widget for Experience/Legacy guests.
 *
 * Renders a scrollable list of missions the host has unlocked with their
 * bundle. Each mission has a timer, a target counter, and a badge tier
 * (🥉 bronze / 🥈 silver / 🥇 gold).
 *
 * Tracking is hybrid:
 *   • "auto" missions listen for `window` custom-events fired by other
 *     pages (PocketBooth capture, MemoryBook submit, etc.) and auto-tick
 *     progress while a timer is running.
 *   • "claim" missions prompt the guest to upload a proof photo/video,
 *     which lands in the main gallery tagged `source=mission` so the host
 *     still sees every shot even if the guest didn't quite nail the task.
 *
 * State (current attempts + earned badges) is persisted in localStorage
 * per (event_id, guest_id) so a refresh doesn't wipe progress. Each
 * completion also POSTs to `/api/events/:id/missions/:mid/complete` for
 * the host-facing leaderboard.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Clock, CheckCircle, X, UploadSimple, Sparkle, Star, CaretDown } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_STYLE = {
  bronze: { label: 'Bronze', emoji: '🥉', color: '#CD7F32', glow: 'rgba(205,127,50,0.35)' },
  silver: { label: 'Silver', emoji: '🥈', color: '#C0C0C0', glow: 'rgba(192,192,192,0.35)' },
  gold:   { label: 'Gold',   emoji: '🥇', color: '#FFD700', glow: 'rgba(255,215,0,0.45)' },
};

const EVT = {
  pocketbooth:       'eventdrop:pocketbooth_capture',
  pocketbooth_party: 'eventdrop:pocketbooth_capture',   // rally counts any capture
  guestbook_entry:   'eventdrop:memorybook_submit',
  video_guestbook:   'eventdrop:video_guestbook_submit',
  photo_upload:      'eventdrop:photo_upload',
  challenge_upload:  'eventdrop:challenge_upload',
};

// Get or mint a persistent per-device guest ID so the same phone doesn't
// claim the same badge twice across refreshes.
const ensureGuestId = () => {
  try {
    let id = localStorage.getItem('eventdrop_guest_id');
    if (!id) {
      id = 'g_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
      localStorage.setItem('eventdrop_guest_id', id);
    }
    return id;
  } catch { return 'g_anon'; }
};

const storeKey = (eventId) => `eventdrop_missions_${eventId}`;

const loadState = (eventId) => {
  try {
    const raw = localStorage.getItem(storeKey(eventId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const saveState = (eventId, state) => {
  try { localStorage.setItem(storeKey(eventId), JSON.stringify(state)); } catch {}
};

const fmtTime = (secs) => {
  if (secs <= 0) return '0:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const MegaMissions = ({ event, guestName = '' }) => {
  const [missions, setMissions] = useState([]);
  const [unlocked, setUnlocked] = useState(false);
  const [state, setState] = useState(() => event ? loadState(event.event_id) : {});
  const [now, setNow] = useState(Date.now());
  const [expanded, setExpanded] = useState(true);
  const [celebrating, setCelebrating] = useState(null);
  const [goldenHour, setGoldenHour] = useState({ active: false, seconds_left: 0 });
  const [squads, setSquads] = useState([]);
  const [mySquadId, setMySquadId] = useState(() => {
    try { return localStorage.getItem(`eventdrop_squad_${event?.event_id || ''}`) || null; }
    catch { return null; }
  });
  const fileRefs = useRef({});
  const guestId = useMemo(() => ensureGuestId(), []);

  // Load catalog + golden-hour + squads
  useEffect(() => {
    if (!event?.event_id) return;
    axios.get(`${API}/events/${event.event_id}/missions`).then(r => {
      setMissions(r.data.missions || []);
      setUnlocked(!!r.data.unlocked);
    }).catch(() => {});
    axios.get(`${API}/events/${event.event_id}/missions/squads`).then(r => {
      setSquads(r.data.squads || []);
    }).catch(() => {});
  }, [event?.event_id]);

  // Poll golden-hour every 20s (and re-fetch on mount). Tight loop during
  // active boost so the countdown stays accurate.
  useEffect(() => {
    if (!event?.event_id) return;
    let stopped = false;
    const fetchGH = async () => {
      try {
        const r = await axios.get(`${API}/events/${event.event_id}/missions/golden-hour`);
        if (!stopped) setGoldenHour(r.data || { active: false, seconds_left: 0 });
      } catch {}
    };
    fetchGH();
    const poll = goldenHour.active ? 5000 : 30000;
    const iv = setInterval(fetchGH, poll);
    return () => { stopped = true; clearInterval(iv); };
  }, [event?.event_id, goldenHour.active]);

  // Tick every second while ANY mission is active (drives countdown UI).
  useEffect(() => {
    const anyActive = Object.values(state).some(s => s?.status === 'active');
    if (!anyActive) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [state]);

  // Persist state on change
  useEffect(() => {
    if (event?.event_id) saveState(event.event_id, state);
  }, [event?.event_id, state]);

  const completeMission = useCallback(async (mission) => {
    setState(prev => {
      if (prev[mission.id]?.status === 'done') return prev;
      return { ...prev, [mission.id]: { ...(prev[mission.id] || {}), status: 'done', completedAt: Date.now() } };
    });
    setCelebrating(mission);
    setTimeout(() => setCelebrating(null), 3200);
    // Fire-and-forget badge log
    try {
      await axios.post(`${API}/events/${event.event_id}/missions/${mission.id}/complete`, {
        guest_id: guestId,
        guest_name: guestName || undefined,
        squad_id: mySquadId || undefined,
      });
    } catch {}
  }, [event, guestId, guestName, mySquadId]);

  // ── Auto-track handler: when another page dispatches an action event,
  // bump progress on every ACTIVE auto-mission whose `action` matches.
  const handleAction = useCallback((action) => {
    setState(prev => {
      const next = { ...prev };
      let mutated = false;
      for (const m of missions) {
        if (m.track !== 'auto' || m.action !== action) continue;
        const s = next[m.id];
        if (!s || s.status !== 'active') continue;
        if (Date.now() > s.deadline) continue;  // expired
        const prog = (s.progress || 0) + 1;
        if (prog >= (m.target || 1)) {
          next[m.id] = { ...s, progress: prog, status: 'done', completedAt: Date.now() };
          setCelebrating(m);
          setTimeout(() => setCelebrating(null), 3200);
          // Fire backend log (no-await)
          axios.post(`${API}/events/${event.event_id}/missions/${m.id}/complete`, {
            guest_id: guestId, guest_name: guestName || undefined, squad_id: mySquadId || undefined,
          }).catch(() => {});
        } else {
          next[m.id] = { ...s, progress: prog };
        }
        mutated = true;
      }
      return mutated ? next : prev;
    });
  }, [missions, event?.event_id, guestId, guestName, mySquadId]);

  // Subscribe to all dispatched action events once missions load
  useEffect(() => {
    if (!missions.length) return;
    const actionSet = new Set(missions.filter(m => m.track === 'auto').map(m => m.action));
    const eventNames = new Set();
    const map = {};  // event-name → action id
    for (const act of actionSet) {
      const en = EVT[act];
      if (!en) continue;
      eventNames.add(en);
      // One event name can map to multiple actions (e.g. pocketbooth fires both
      // pocketbooth + pocketbooth_party). Keep both, handle in loop below.
      map[en] = map[en] || [];
      map[en].push(act);
    }
    const handlers = [];
    for (const en of eventNames) {
      const h = () => map[en].forEach(handleAction);
      window.addEventListener(en, h);
      handlers.push([en, h]);
    }
    return () => handlers.forEach(([en, h]) => window.removeEventListener(en, h));
  }, [missions, handleAction]);

  const startMission = (mission) => {
    const deadline = Date.now() + (mission.timer || 300) * 1000;
    setState(prev => ({
      ...prev,
      [mission.id]: { status: 'active', startedAt: Date.now(), deadline, progress: 0 },
    }));
    toast(`${mission.emoji} Mission started — ${Math.round((mission.timer || 300)/60)} min on the clock!`, {
      description: mission.desc,
    });
  };

  const retryMission = (mission) => {
    setState(prev => {
      const next = { ...prev };
      delete next[mission.id];
      return next;
    });
  };

  const handleClaimUpload = async (mission, file) => {
    if (!file || !event) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/events/${event.event_id}/photos?source=mission`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      await completeMission(mission);
      toast.success(`${mission.emoji} ${mission.title} — claimed!`);
    } catch {
      toast.error('Upload failed — try again');
    }
  };

  // ── Expiry sweep: any "active" whose deadline passed → status "failed"
  useEffect(() => {
    setState(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [mid, s] of Object.entries(prev)) {
        if (s?.status === 'active' && Date.now() > s.deadline) {
          next[mid] = { ...s, status: 'failed' };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [now]);

  if (!unlocked) return null;  // host hasn't purchased Experience/Legacy/Challenges

  const activeList   = missions.filter(m => state[m.id]?.status === 'active');
  const availList    = missions.filter(m => !state[m.id]);
  const completedList = missions.filter(m => state[m.id]?.status === 'done');
  const failedList   = missions.filter(m => state[m.id]?.status === 'failed');

  const totalBadges = completedList.length;
  const goldEarned = completedList.filter(m => m.badge === 'gold').length;
  const silverEarned = completedList.filter(m => m.badge === 'silver').length;
  const bronzeEarned = completedList.filter(m => m.badge === 'bronze').length;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(108,85,224,0.14), rgba(194,157,82,0.06))',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
      data-testid="mega-missions"
    >
      {/* ═══ HEADER ═══ */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors"
        data-testid="mega-missions-toggle"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #FFD700, #FF9100)', boxShadow: '0 6px 20px rgba(255,215,0,0.25)' }}
          >
            <Trophy size={22} weight="fill" className="text-white" />
          </div>
          <div className="text-left">
            <p className="text-white font-black text-base leading-tight flex items-center gap-2">
              Mega Missions
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/30">
                NEW
              </span>
            </p>
            <p className="text-white/40 text-xs">
              {totalBadges > 0
                ? `🥇${goldEarned} 🥈${silverEarned} 🥉${bronzeEarned} earned`
                : 'Timed quests · win badges'}
            </p>
          </div>
        </div>
        <CaretDown size={18} weight="bold" className={`text-white/40 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3" data-testid="mega-missions-body">
          {/* ═══ GOLDEN HOUR BANNER ═══ */}
          {goldenHour.active && (
            <div
              className="relative rounded-xl overflow-hidden p-3"
              style={{
                background: 'linear-gradient(90deg, #FFD700, #FF9100, #FFD700)',
                backgroundSize: '200% 100%',
                animation: 'ghShimmer 3s ease-in-out infinite',
              }}
              data-testid="golden-hour-banner"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkle size={18} weight="fill" className="text-white" />
                  <div>
                    <p className="text-white font-black text-sm leading-tight">GOLDEN HOUR · 2× POINTS</p>
                    <p className="text-white/90 text-[11px] font-semibold">Finish any mission for double pts!</p>
                  </div>
                </div>
                <p className="text-white font-mono font-black text-sm tabular-nums">
                  {fmtTime(goldenHour.seconds_left || 0)}
                </p>
              </div>
            </div>
          )}

          {/* ═══ SQUAD PICKER ═══ */}
          {squads.length > 0 && !mySquadId && (
            <div
              className="rounded-xl p-3"
              style={{ background: 'rgba(194,157,82,0.08)', border: '1px solid rgba(194,157,82,0.25)' }}
              data-testid="squad-picker"
            >
              <p className="text-white font-bold text-sm mb-0.5">Pick your squad</p>
              <p className="text-white/50 text-xs mb-3">Compete for the team crown — your badges count for your squad.</p>
              <div className="flex flex-wrap gap-2">
                {squads.map(s => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setMySquadId(s.id);
                      try { localStorage.setItem(`eventdrop_squad_${event.event_id}`, s.id); } catch {}
                    }}
                    className="px-3 py-2 rounded-xl text-sm font-bold text-white flex items-center gap-1.5 transition-all hover:scale-[1.03]"
                    style={{ background: s.color || '#B89762' }}
                    data-testid={`squad-pick-${s.id}`}
                  >
                    <span>{s.emoji}</span>
                    <span>{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {squads.length > 0 && mySquadId && (() => {
            const s = squads.find(x => x.id === mySquadId);
            if (!s) return null;
            return (
              <div className="flex items-center justify-between text-xs text-white/60 px-1">
                <span>Representing <b className="text-white">{s.emoji} {s.name}</b></span>
                <button
                  onClick={() => {
                    setMySquadId(null);
                    try { localStorage.removeItem(`eventdrop_squad_${event.event_id}`); } catch {}
                  }}
                  className="text-white/40 underline decoration-dotted"
                  data-testid="squad-switch"
                >
                  Switch
                </button>
              </div>
            );
          })()}

          {/* Active list first */}
          {activeList.map(m => (
            <ActiveCard key={m.id} mission={m} state={state[m.id]} now={now}
              onClaim={() => fileRefs.current[m.id]?.click()}
              onAutoComplete={() => completeMission(m)}
              fileRef={el => { fileRefs.current[m.id] = el; }}
              onFile={(f) => handleClaimUpload(m, f)}
            />
          ))}

          {/* Available */}
          {availList.length > 0 && (
            <>
              <p className="text-white/30 text-[11px] font-bold uppercase tracking-wider mt-1 px-1">
                Tap a mission to start the timer
              </p>
              {availList.map(m => (
                <AvailCard key={m.id} mission={m} onStart={() => startMission(m)} />
              ))}
            </>
          )}

          {/* Failed (retryable) */}
          {failedList.length > 0 && (
            <>
              <p className="text-red-300/70 text-[11px] font-bold uppercase tracking-wider mt-2 px-1">
                Time's up — retry?
              </p>
              {failedList.map(m => (
                <FailedCard key={m.id} mission={m} onRetry={() => retryMission(m)} />
              ))}
            </>
          )}

          {/* Completed */}
          {completedList.length > 0 && (
            <>
              <p className="text-emerald-300/70 text-[11px] font-bold uppercase tracking-wider mt-2 px-1">
                Badges won ({completedList.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {completedList.map(m => (
                  <div key={m.id}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold"
                    style={{
                      background: `${BADGE_STYLE[m.badge].color}20`,
                      border: `1px solid ${BADGE_STYLE[m.badge].color}50`,
                      color: BADGE_STYLE[m.badge].color,
                    }}
                    data-testid={`mission-badge-${m.id}`}
                  >
                    <span>{m.emoji}</span>
                    <span className="text-white/80">{m.title}</span>
                    <span>{BADGE_STYLE[m.badge].emoji}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ CELEBRATION OVERLAY ═══ */}
      {celebrating && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          data-testid="mission-celebrate"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" style={{ animation: 'mmFade .3s ease-out' }} />
          <div
            className="relative text-center px-8 py-10 rounded-3xl"
            style={{
              background: 'linear-gradient(180deg, #15161B, #15161B)',
              border: `2px solid ${BADGE_STYLE[celebrating.badge].color}`,
              boxShadow: `0 0 80px ${BADGE_STYLE[celebrating.badge].glow}`,
              animation: 'mmPop .6s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            <div className="text-7xl mb-3" style={{ animation: 'mmSpin 1.2s ease-out' }}>
              {BADGE_STYLE[celebrating.badge].emoji}
            </div>
            <p className="text-white/40 text-sm font-bold uppercase tracking-widest mb-1">
              {BADGE_STYLE[celebrating.badge].label} Badge
            </p>
            <p className="text-white text-2xl font-black mb-2">{celebrating.emoji} {celebrating.title}</p>
            <p className="text-amber-300 text-lg font-bold">+{celebrating.points} pts</p>
          </div>
          {/* Emoji shower */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {Array.from({ length: 18 }).map((_, i) => (
              <span key={i}
                className="absolute text-3xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: '-10%',
                  animation: `mmFall ${2 + Math.random() * 1.5}s ease-in forwards`,
                  animationDelay: `${Math.random() * 0.5}s`,
                }}
              >
                {BADGE_STYLE[celebrating.badge].emoji}
              </span>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes mmFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes mmPop  { 0% { opacity: 0; transform: scale(0.6) } 60% { opacity: 1; transform: scale(1.08) } 100% { transform: scale(1) } }
        @keyframes mmSpin { 0% { transform: rotate(-20deg) scale(0.5) } 60% { transform: rotate(15deg) scale(1.15) } 100% { transform: rotate(0) scale(1) } }
        @keyframes mmFall { from { transform: translateY(0) rotate(0) } to { transform: translateY(110vh) rotate(720deg) } }
        @keyframes ghShimmer { 0% { background-position: 0% 0 } 100% { background-position: 200% 0 } }
      `}</style>
    </div>
  );
};

// ── SUB-COMPONENTS ─────────────────────────────────────

const ActiveCard = ({ mission, state, now, onClaim, onAutoComplete, fileRef, onFile }) => {
  const remaining = Math.max(0, Math.floor((state.deadline - now) / 1000));
  const target = mission.target || 1;
  const progress = state.progress || 0;
  const pct = Math.min(100, (progress / target) * 100);
  const isAuto = mission.track === 'auto';
  const style = BADGE_STYLE[mission.badge];

  return (
    <div
      className="rounded-xl p-3.5"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${style.color}50`,
        boxShadow: `0 0 20px ${style.glow}`,
      }}
      data-testid={`mission-active-${mission.id}`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none">{mission.emoji}</span>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm leading-tight">{mission.title}</p>
          <p className="text-white/40 text-xs mt-0.5 leading-snug">{mission.desc}</p>
        </div>
        <div className="flex items-center gap-1 text-xs font-bold" style={{ color: style.color }}>
          <Clock size={14} weight="bold" /> {fmtTime(remaining)}
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${style.color}, #C29D52)` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-white/40 text-xs">
          {isAuto ? `${progress}/${target} done` : 'Upload proof when complete'}
        </p>
        {isAuto ? (
          <p className="text-white/30 text-[10px] italic">auto-tracking…</p>
        ) : (
          <>
            <button
              onClick={onClaim}
              className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all hover:scale-105"
              style={{ background: style.color, color: '#15161B' }}
              data-testid={`mission-claim-${mission.id}`}
            >
              <UploadSimple size={13} weight="bold" /> Claim
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              capture="environment"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }}
            />
          </>
        )}
      </div>
    </div>
  );
};

const AvailCard = ({ mission, onStart }) => {
  const style = BADGE_STYLE[mission.badge];
  return (
    <button
      onClick={onStart}
      className="w-full rounded-xl p-3.5 flex items-center gap-3 text-left transition-all hover:scale-[1.01] hover:bg-white/5"
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      data-testid={`mission-available-${mission.id}`}
    >
      <span className="text-2xl leading-none">{mission.emoji}</span>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm leading-tight">{mission.title}</p>
        <p className="text-white/40 text-xs mt-0.5 leading-snug line-clamp-2">{mission.desc}</p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: `${style.color}22`, color: style.color, border: `1px solid ${style.color}40` }}
        >
          {style.emoji} {style.label}
        </span>
        <span className="text-white/30 text-[10px]"><Clock size={10} weight="bold" className="inline mr-0.5" />{Math.round((mission.timer || 300)/60)}m</span>
      </div>
    </button>
  );
};

const FailedCard = ({ mission, onRetry }) => (
  <div
    className="rounded-xl p-3.5 flex items-center gap-3 opacity-60"
    style={{ background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.2)' }}
    data-testid={`mission-failed-${mission.id}`}
  >
    <span className="text-2xl leading-none grayscale">{mission.emoji}</span>
    <div className="flex-1 min-w-0">
      <p className="text-white/70 font-bold text-sm line-through">{mission.title}</p>
      <p className="text-red-300/70 text-xs">Timed out</p>
    </div>
    <button
      onClick={onRetry}
      className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white/10 text-white hover:bg-white/20"
      data-testid={`mission-retry-${mission.id}`}
    >
      Retry
    </button>
  </div>
);

export default MegaMissions;
